import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, Calendar, Clock, User, FileText, Eye, Globe, Lock, Building2,
  Link2 as LinkIcon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const REJECT_TITLES = ['test', 'event', 'meeting', 'untitled', 'activity', 'tbd', 'n/a', 'asdf', 'aaa'];
const REJECT_TITLE_STARTS = ['join us', 'best', 'exclusive', "don't miss", 'amazing', 'incredible', 'limited'];
const REJECT_LOCATIONS = ['park', 'outside', 'here', 'there', 'somewhere', 'tbd', 'n/a', 'idk'];

export default function ActivityCreate() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});

  // Institution context from URL params
  const sourceType = searchParams.get('source_type');
  const sourceId = searchParams.get('source_id');
  const isInstitutional = sourceType === 'institution' && sourceId;

  const [form, setForm] = useState({
    title: '',
    description: '',
    what_to_know: '',
    location: '',
    category: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    host_name: '',
    activity_type: 'in_person',
    join_link: '',
    visibility: isInstitutional ? 'institution' : ''
  });

  useEffect(() => {
    axios.get(`${API}/api/categories/main`).then(res => {
      const cats = Array.isArray(res.data) ? res.data : (res.data?.categories || []);
      setCategories(cats);
    }).catch(() => {});
  }, []);

  // Hydrate from a "Do this again" draft if one is waiting in sessionStorage.
  // Pre-fills title/location/description/etc. — user only adjusts date/time.
  useEffect(() => {
    if (searchParams.get('from') !== 'duplicate') return;
    let raw = null;
    try { raw = sessionStorage.getItem('lk_duplicate_draft'); } catch { return; }
    if (!raw) return;
    try {
      const draft = JSON.parse(raw);
      setForm(prev => ({
        ...prev,
        title: draft.title || '',
        description: draft.description || '',
        what_to_know: draft.what_to_know || '',
        location: draft.location || '',
        category: draft.category || '',
        host_name: draft.host_name || '',
        activity_type: draft.activity_type || 'in_person',
        join_link: draft.join_link || '',
        visibility: draft.visibility || 'public',
      }));
      try { sessionStorage.removeItem('lk_duplicate_draft'); } catch { /* ignore */ }
    } catch { /* malformed draft — ignore */ }
  }, [searchParams]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const e = {};
    const title = form.title.trim().toLowerCase();
    if (!form.title.trim() || form.title.trim().length < 5) e.title = 'Add a clear title';
    else if (REJECT_TITLES.includes(title)) e.title = 'Add a clear title';
    else if (REJECT_TITLE_STARTS.some(s => title.startsWith(s))) e.title = 'Use plain language — no promotional tone';
    if (!form.start_date || !form.start_time) e.time = 'Add a time';
    if (form.activity_type === 'virtual') {
      const jl = (form.join_link || '').trim();
      if (!jl || !/^https?:\/\//i.test(jl)) e.join_link = 'Add a valid https:// link';
    } else {
      const loc = form.location.trim().toLowerCase();
      if (!form.location.trim() || form.location.trim().length < 4) e.location = 'Add a location';
      else if (REJECT_LOCATIONS.includes(loc)) e.location = 'Add a location';
    }
    if (!form.category) e.category = 'Select a category';
    if (!form.visibility) e.visibility = 'Select visibility';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');

      const payload = {
        title: form.title,
        description: form.description,
        what_to_know: form.what_to_know || null,
        location: form.location,
        category: form.category,
        host_name: form.host_name,
        visibility: form.visibility,
        activity_type: form.activity_type,
        join_link: form.activity_type === 'virtual' ? form.join_link : null,
      };

      const startStr = `${form.start_date}T${form.start_time}:00`;
      payload.start_time = new Date(startStr).toISOString();

      if (form.end_date) {
        const endStr = form.end_time
          ? `${form.end_date}T${form.end_time}:00`
          : `${form.end_date}T23:59:00`;
        payload.end_time = new Date(endStr).toISOString();
      }

      let res;
      if (isInstitutional) {
        res = await axios.post(`${API}/api/inst-admin/${sourceId}/activities`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        if (sourceType && sourceId) {
          payload.source_type = sourceType;
          payload.source_id = sourceId;
        }
        res = await axios.post(`${API}/api/activities/`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success('Activity created');
      const actId = res.data.activity_id || res.data.activity?.activity_id;
      // User-initiated share (not forced): flag a one-time invite to share on landing
      if (actId) {
        sessionStorage.setItem('lk_just_created_activity', actId);
      }
      if (isInstitutional) {
        navigate(`/institutions/${sourceId}/dashboard`);
      } else {
        navigate(`/activities/${actId}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create activity');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4 flex items-center h-14 gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:opacity-70" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
          </button>
          <div>
            <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>
              {isInstitutional ? 'Create Institution Activity' : 'Create Activity'}
            </h1>
            <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
              {isInstitutional ? 'This activity will be owned by the institution.' : 'Start coordinating something real.'}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--fy-text-primary)' }}>
              Title <span style={{ color: 'var(--lk-terra)' }}>*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="What are you organizing?"
              maxLength={120}
              className="w-full px-4 py-3 rounded-xl text-sm outline-none"
              style={{
                backgroundColor: 'var(--fy-surface-card)',
                border: `1px solid ${errors.title ? 'var(--lk-terra)' : 'var(--fy-border-subtle)'}`,
                color: 'var(--fy-text-primary)'
              }}
              data-testid="activity-title-input"
            />
            {errors.title && <p className="text-xs mt-1" style={{ color: 'var(--lk-terra)' }} data-testid="error-title">{errors.title}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--fy-text-primary)' }}>
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="What should people know about this activity?"
              rows={4}
              maxLength={2000}
              className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none"
              style={{
                backgroundColor: 'var(--fy-surface-card)',
                border: '1px solid var(--fy-border-subtle)',
                color: 'var(--fy-text-primary)'
              }}
              data-testid="activity-description-input"
            />
          </div>

          {/* What to know — optional, practical notes */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--fy-text-primary)' }}>
              What to know <span className="font-normal" style={{ color: 'var(--fy-text-disabled)' }}>· optional</span>
            </label>
            <textarea
              value={form.what_to_know}
              onChange={(e) => handleChange('what_to_know', e.target.value)}
              placeholder="Parking. Bathrooms. Kid-friendly. What to bring."
              rows={3}
              maxLength={800}
              className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none"
              style={{
                backgroundColor: 'var(--fy-surface-card)',
                border: '1px solid var(--fy-border-subtle)',
                color: 'var(--fy-text-primary)'
              }}
              data-testid="activity-what-to-know-input"
            />
            <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
              Short, practical notes only. No opinions or comparisons.
            </p>
          </div>

          {/* Date & Time Row */}
          <Card className="p-4 space-y-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: `1px solid ${errors.time ? 'var(--lk-terra)' : 'var(--fy-border-subtle)'}` }}>
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4" style={{ color: 'var(--lk-secondary-orange)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Date & Time <span style={{ color: 'var(--lk-terra)' }}>*</span></span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--fy-text-secondary)' }}>Start Date</label>
                <input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                  data-testid="activity-start-date"
                />
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--fy-text-secondary)' }}>Start Time</label>
                <input
                  type="time"
                  value={form.start_time}
                  onChange={(e) => handleChange('start_time', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                  data-testid="activity-start-time"
                />
              </div>
            </div>
            {errors.time && <p className="text-xs" style={{ color: 'var(--lk-terra)' }} data-testid="error-time">{errors.time}</p>}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--fy-text-secondary)' }}>End Date</label>
                <input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => handleChange('end_date', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                  data-testid="activity-end-date"
                />
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--fy-text-secondary)' }}>End Time</label>
                <input
                  type="time"
                  value={form.end_time}
                  onChange={(e) => handleChange('end_time', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                  data-testid="activity-end-time"
                />
              </div>
            </div>
          </Card>

          {/* Activity type toggle — In-person vs Virtual */}
          <div>
            <div className="inline-flex rounded-full p-1" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }} data-testid="activity-type-toggle">
              {[
                { id: 'in_person', label: 'In-person' },
                { id: 'virtual', label: 'Virtual' },
              ].map(opt => {
                const selected = form.activity_type === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => handleChange('activity_type', opt.id)}
                    className="text-xs font-semibold px-4 py-1.5 rounded-full transition-colors"
                    style={{
                      backgroundColor: selected ? 'var(--lk-sage)' : 'transparent',
                      color: selected ? '#fff' : 'var(--fy-text-secondary)',
                    }}
                    data-testid={`activity-type-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Location (in-person) OR Join link (virtual) */}
          {form.activity_type === 'virtual' ? (
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--fy-text-primary)' }}>
                <LinkIcon className="w-4 h-4 inline mr-1" style={{ color: 'var(--lk-sage)' }} />
                Join link <span style={{ color: 'var(--lk-terra)' }}>*</span>
              </label>
              <input
                type="url"
                value={form.join_link}
                onChange={(e) => handleChange('join_link', e.target.value)}
                placeholder="https://zoom.us/j/… or meet.google.com/…"
                className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                style={{
                  backgroundColor: 'var(--fy-surface-card)',
                  border: `1px solid ${errors.join_link ? 'var(--lk-terra)' : 'var(--fy-border-subtle)'}`,
                  color: 'var(--fy-text-primary)'
                }}
                data-testid="activity-join-link-input"
              />
              <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
                A one-time link for this meeting. LikeKind isn’t for always-open rooms.
              </p>
              {errors.join_link && <p className="text-xs mt-1" style={{ color: 'var(--lk-terra)' }}>{errors.join_link}</p>}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--fy-text-primary)' }}>
                <MapPin className="w-4 h-4 inline mr-1" style={{ color: 'var(--lk-sage)' }} />
                Location <span style={{ color: 'var(--lk-terra)' }}>*</span>
              </label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Where is this happening? (e.g. Elm Park – South Lawn)"
                className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                style={{
                  backgroundColor: 'var(--fy-surface-card)',
                  border: `1px solid ${errors.location ? 'var(--lk-terra)' : 'var(--fy-border-subtle)'}`,
                  color: 'var(--fy-text-primary)'
                }}
                data-testid="activity-location-input"
              />
              {errors.location && <p className="text-xs mt-1" style={{ color: 'var(--lk-terra)' }} data-testid="error-location">{errors.location}</p>}
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--fy-text-primary)' }}>
              Category <span style={{ color: 'var(--lk-terra)' }}>*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleChange('category', cat.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all"
                  style={{
                    backgroundColor: form.category === cat.id ? 'var(--lk-sage-muted, #EDF1E8)' : 'var(--fy-surface-card)',
                    border: `2px solid ${form.category === cat.id ? 'var(--lk-sage)' : (errors.category ? 'var(--lk-terra)' : 'var(--fy-border-subtle)')}`,
                    color: form.category === cat.id ? 'var(--lk-sage)' : 'var(--fy-text-primary)',
                    fontWeight: form.category === cat.id ? 600 : 400
                  }}
                  data-testid={`category-select-${cat.id}`}
                >
                  <span>{cat.icon}</span>
                  <span>{cat.name}</span>
                </button>
              ))}
            </div>
            {errors.category && <p className="text-xs mt-1" style={{ color: 'var(--lk-terra)' }} data-testid="error-category">{errors.category}</p>}
          </div>

          {/* Host */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--fy-text-primary)' }}>
              <User className="w-4 h-4 inline mr-1" style={{ color: 'var(--lk-sage)' }} />
              Host / Organizer
            </label>
            <input
              type="text"
              value={form.host_name}
              onChange={(e) => handleChange('host_name', e.target.value)}
              placeholder="Who is organizing this?"
              className="w-full px-4 py-3 rounded-xl text-sm outline-none"
              style={{
                backgroundColor: 'var(--fy-surface-card)',
                border: '1px solid var(--fy-border-subtle)',
                color: 'var(--fy-text-primary)'
              }}
              data-testid="activity-host-input"
            />
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--fy-text-primary)' }}>
              <Eye className="w-4 h-4 inline mr-1" style={{ color: 'var(--lk-sage)' }} />
              Visibility <span style={{ color: 'var(--lk-terra)' }}>*</span>
            </label>
            <div className="flex gap-3">
              {[
                { value: 'public', label: 'Public', desc: 'Visible to everyone — appears on calendar and map', icon: Globe },
                ...(isInstitutional ? [{ value: 'institution', label: 'Institution', desc: 'Visible to institution members only', icon: Building2 }] : []),
                { value: 'private', label: 'Private', desc: 'Only visible to you and invited participants', icon: Lock },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleChange('visibility', opt.value)}
                  className="flex-1 p-3 rounded-xl text-left transition-all"
                  style={{
                    backgroundColor: form.visibility === opt.value ? 'var(--lk-sage-muted)' : 'var(--fy-surface-card)',
                    border: `2px solid ${form.visibility === opt.value ? 'var(--lk-sage)' : (errors.visibility ? 'var(--lk-terra)' : 'var(--fy-border-subtle)')}`
                  }}
                  data-testid={`visibility-${opt.value}`}
                >
                  <opt.icon className="w-4 h-4 mb-1" style={{ color: form.visibility === opt.value ? 'var(--lk-sage)' : 'var(--fy-text-disabled)' }} />
                  <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>{opt.label}</p>
                  <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>{opt.desc}</p>
                </button>
              ))}
            </div>
            {errors.visibility && <p className="text-xs mt-1" style={{ color: 'var(--lk-terra)' }} data-testid="error-visibility">{errors.visibility}</p>}
          </div>

          {/* Lifecycle note */}
          <Card className="p-3" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
            <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
              <FileText className="w-3.5 h-3.5 inline mr-1" />
              Activities start as an <strong>Idea</strong>. You can advance the status to Confirmed, Active, and Completed as coordination progresses.
            </p>
          </Card>

          {/* Submit */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 rounded-full text-base font-medium"
            style={{ backgroundColor: 'var(--lk-sage)', color: 'white', opacity: submitting ? 0.6 : 1 }}
            data-testid="create-activity-submit"
          >
            {submitting ? 'Creating...' : 'Create Activity'}
          </Button>
        </form>
      </main>
    </div>
  );
}
